
import React, { useEffect, useRef, useState } from "react";
import { BsFullscreen } from "react-icons/bs";
import { CiPause1, CiVolumeHigh, CiVolumeMute } from "react-icons/ci";
import { HiOutlineCog } from "react-icons/hi";
import {
  IoPlayBackOutline,
  IoPlayForwardOutline,
  IoPlayOutline,
} from "react-icons/io5";
import Timeline from "../components/Timeline";
import VolumeControl from "../components/VolumeControl";




interface VideoProps {
  videoSource: string;
}

const VideoScreen: React.FC<VideoProps> = ({ videoSource }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  // Function to detect if the URL is a YouTube URL
  const isYouTubeUrl = (url: string): boolean => {
    return /youtu\.be|youtube\.com/.test(url);
  };

  // Function to convert YouTube URL to embed format
  const getYouTubeEmbedUrl = (url: string): string | null => {
    const videoIdMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=))([^?&]+)/);
    return videoIdMatch ? `https://www.youtube.com/embed/${videoIdMatch[1]}` : null;
  };

  // Check if the videoSource is a YouTube URL
  const isYouTube = isYouTubeUrl(videoSource);
  const embedUrl = isYouTube ? getYouTubeEmbedUrl(videoSource) : null;

  // Function to detect if the URL is likely an MP4 file
  const isMp4Url = (url: string): boolean => {
    return /\.(mp4)$/i.test(url);
  };

  const isMp4 = isMp4Url(videoSource);

  useEffect(() => {
    if (videoRef.current && !isYouTube) {
      const video = videoRef.current;

      video.addEventListener("canplaythrough", () => {
        setDuration(video.duration);
      });

      video.addEventListener("timeupdate", () => {
        setCurrentTime(video.currentTime);
      });

      video.addEventListener("ended", () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });

      return () => {
        // Cleanup event listeners
        video.removeEventListener("canplaythrough", () => { });
        video.removeEventListener("timeupdate", () => { });
        video.removeEventListener("ended", () => { });
      };
    }
  }, [isYouTube]);

  const handlePlayPause = () => {
    if (videoRef.current && !isYouTube) {
      const video = videoRef.current;
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleBackward = () => {
    if (videoRef.current && !isYouTube) {
      const video = videoRef.current;
      video.currentTime -= 10;
    }
  };

  const handleForward = () => {
    if (videoRef.current && !isYouTube) {
      const video = videoRef.current;
      video.currentTime += 10;
    }
  };

  const handleFullScreen = () => {
    if (videoRef.current && !isYouTube) {
      const video = videoRef.current;
      if (video.requestFullscreen) {
        video.requestFullscreen();
      }
    }
  };

  const handleTimeChange = (newTime: number) => {
    if (videoRef.current && !isYouTube) {
      const video = videoRef.current;
      video.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (volume: number) => {
    if (videoRef.current && !isYouTube) {
      const video = videoRef.current;
      const newVolume = parseFloat(volume.toString());
      video.volume = newVolume;
      setVolume(newVolume);
    }
  };

  if (isYouTube && embedUrl) {
    return (
      <div className="rounded-xl bg-gray-300 bg-center bg-cover bg-no-repeat shadow-[0_2px_8px_0_rgba(33,30,55,0.1)] 
                w-full h-[200px] tablet:h-[300px] laptop:h-[400px] desktop:h-[450px] largescreen:h-[500px] xxl:h-[600px]"
      >
        <iframe
          className="w-full h-full rounded-md"
          src={embedUrl}
          title="YouTube Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>

    );
  }

  if (isYouTube && !embedUrl) {
    return (
      <div className="bg-[#E9E0FA] p-20 video-player relative max-w-xl mx-auto">
        <div className="text-red-500">Error: Invalid YouTube URL</div>
      </div>
    );
  }

  if (isMp4) {
    return (
      <div className="relative w-full max-w-4xl mx-auto rounded-xl overflow-hidden bg-black">
        <video
          className="w-full h-auto rounded-md"
          ref={videoRef}
        >
          <source src={videoSource} type="video/mp4" />
        </video>
        <div className="absolute bottom-0 left-0 w-full px-3 pb-2 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
          <div className="flex items-center justify-between text-xs text-white mb-1">
            <Timeline
              currentTime={currentTime}
              duration={duration}
              onTimeChange={handleTimeChange}
            />
          </div>
          <div className="flex items-center justify-between bg-[#3e4043db] rounded-xl px-4 py-2">
            <VolumeControl
              volume={volume}
              onVolumeChange={handleVolumeChange}
              onToggleMute={(isMuted) => {
                isMuted ? handleVolumeChange(0) : handleVolumeChange(1);
              }}
            />
            <div className="flex items-center space-x-2">
              <button onClick={handleBackward}>
                <IoPlayBackOutline size={24} />
              </button>
              <button onClick={handlePlayPause}>
                {isPlaying ? <CiPause1 size={28} /> : <IoPlayOutline size={28} />}
              </button>
              <button onClick={handleForward}>
                <IoPlayForwardOutline size={24} />
              </button>
            </div>
            <div className="flex items-center space-x-3">
              <button onClick={handleFullScreen}>
                <BsFullscreen size={18} />
              </button>
              <button>
                <HiOutlineCog size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="bg-[#E9E0FA] p-20 video-player relative max-w-xl mx-auto">
      <div className="text-red-500">Error: Unsupported video format</div>
    </div>
  );
};

export default VideoScreen;
